import { useEffect, useMemo, useState } from "react";
import { api, fileUrl, type Message, type SearchResult, type ThreadItem } from "../api";
import { go, loadRoom, setRead, useStore, inChat } from "../store";
import { dayLabel, plain, Rich, timeLabel } from "../text";
import { Avatar, roomTitle, sizeLabel, toast, useMe } from "./ui";

/** One message as a result row: who, where, when; click opens it in its chat. */
function Hit({ m, q }: { m: Message; q?: string }) {
  const me = useMe();
  const users = useStore((s) => s.users);
  const room = useStore((s) => s.rooms[m.roomId]);
  const names = useMemo(() => Object.values(users).map((u) => u.name), [users]);
  const open = () => go({ v: "room", id: m.roomId, focus: m.id, thread: m.parentId && !m.alsoInChat ? m.parentId : undefined });
  return (
    <button className="hit" onClick={open}>
      <Avatar user={users[m.userId]} size={28} />
      <span className="hb">
        <span className="hw"><b>{users[m.userId]?.name}</b><span>{roomTitle(room, me.id, users)}{m.parentId ? " · in a thread" : ""}</span><small>{dayLabel(m.createdAt)} {timeLabel(m.createdAt)}</small></span>
        <span className="ht">{q ? <Mark text={plain(m.body)} q={q} /> : <Rich text={m.body} names={names} me={me.name} />}</span>
      </span>
    </button>
  );
}
function Mark({ text, q }: { text: string; q: string }) {
  const words = q.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
  if (!words.length) return <>{text}</>;
  const re = new RegExp(`(${words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "giu");
  return <>{text.split(re).map((p, i) => (i % 2 ? <mark key={i}>{p}</mark> : p))}</>;
}

function Page({ title, children, right }: { title: string; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="col">
      <div className="head"><b>{title}</b>{right && <span className="r">{right}</span>}</div>
      <div className="thread page">{children}</div>
    </div>
  );
}

export function SearchPage({ q }: { q: string }) {
  const [text, setText] = useState(q);
  const [res, setRes] = useState<SearchResult | null>(null);
  const [busy, setBusy] = useState(false);
  const users = useStore((s) => s.users);
  useEffect(() => { setText(q); if (!q.trim()) return setRes(null); setBusy(true); api.search(q).then(setRes).catch(() => toast("Search failed")).finally(() => setBusy(false)); }, [q]);
  const total = res ? res.messages.length + res.tasks.length + res.files.length : 0;
  return (
    <div className="col">
      <div className="head"><b>Search</b>
        <input className="sbox" autoFocus value={text} placeholder="Messages, tasks and files" onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && go({ v: "search", q: text.trim() }, true)} />
      </div>
      <div className="thread page">
        {busy && <div className="empty">Searching…</div>}
        {res && !busy && !total && <div className="empty">Nothing found for “{q}”.</div>}
        {res && res.tasks.length > 0 && <><h4>Tasks</h4>{res.tasks.map((t) => (
          <button key={t.id} className="hit" onClick={() => go({ v: "task", id: t.id })}><span className="tick">{t.status === "done" ? "✓" : "○"}</span>
            <span className="hb"><span className="ht"><Mark text={t.title} q={q} /></span><span className="hw"><span>{users[t.ownerId]?.name}{t.status === "done" ? " · done" : ""}</span></span></span></button>))}</>}
        {res && res.messages.length > 0 && <><h4>Messages</h4>{res.messages.map((m) => <Hit key={m.id} m={m} q={q} />)}</>}
        {res && res.files.length > 0 && <><h4>Files</h4>{res.files.map((f) => (
          <div key={f.id} className="hit">
            <span className="tick">📎</span>
            <span className="hb"><span className="ht"><a href={fileUrl(f.id, true)} download={f.name}><Mark text={f.name} q={q} /></a> <small>{sizeLabel(f.size)}</small></span>
              <span className="hw"><span>{users[f.userId]?.name} · {dayLabel(f.createdAt)}</span><button className="lnk" onClick={() => go({ v: "room", id: f.roomId, focus: f.messageId })}>Show in chat</button></span></span>
          </div>))}</>}
      </div>
    </div>
  );
}

export function MentionsPage() {
  const [list, setList] = useState<Message[] | null>(null);
  const seen = useStore((s) => s.mentionsSeenId);
  useEffect(() => {
    api.mentions().then((l) => {
      setList(l);
      // Opening the page counts them as seen.
      const top = l[0]?.id ?? 0;
      if (top > useStore.getState().mentionsSeenId) { useStore.setState({ mentionsSeenId: top }); api.seeMentions(top).catch(() => {}); }
    }).catch(() => toast("Couldn't load mentions"));
  }, []);
  return (
    <Page title="Mentions">
      {list && !list.length && <div className="empty">When someone writes @you, it shows here.</div>}
      {list?.map((m) => <div key={m.id} className={m.id > seen ? "fresh" : ""}><Hit m={m} /></div>)}
    </Page>
  );
}

export function ThreadsPage() {
  const [list, setList] = useState<ThreadItem[] | null>(null);
  const follows = useStore((s) => s.follows);
  useEffect(() => { api.threads().then(setList).catch(() => toast("Couldn't load threads")); }, []);
  return (
    <Page title="Threads">
      {list && !list.length && <div className="empty">Threads you start, reply to or are @mentioned in show here.</div>}
      {list?.map(({ root, replies, follow }) => {
        const f = follows[root.id] ?? follow;
        const unread = f.lastReplyId > f.lastReadId;
        return (
          <div key={root.id} className={`tcard${unread ? " fresh" : ""}`}>
            <Hit m={root} />
            {replies.map((r) => <div key={r.id} className="treply"><Hit m={r} /></div>)}
            <button className="lnk open" onClick={() => go({ v: "room", id: root.roomId, thread: root.id })}>{unread ? "New replies · open thread" : "Open thread"} ›</button>
          </div>
        );
      })}
    </Page>
  );
}

export function LaterPage() {
  const saved = useStore((s) => s.saved);
  const byId = useStore((s) => s.byId);
  const items = Object.values(saved).sort((a, b) => (a.remindAt ?? "9").localeCompare(b.remindAt ?? "9") || b.createdAt.localeCompare(a.createdAt));
  useEffect(() => {
    for (const s of items) if (!byId[s.messageId]) api.thread(s.messageId).then((t) => useStore.setState((x) => ({ byId: { ...x.byId, [t.root.id]: t.root, ...Object.fromEntries(t.replies.map((r) => [r.id, r])) } }))).catch(() => {});
  }, [items.length]);
  const done = async (id: number) => {
    try { useStore.setState({ saved: Object.fromEntries((await api.save(id, null, false)).map((x) => [x.messageId, x])) }); } catch { toast("Didn't work"); }
  };
  return (
    <Page title="Later">
      {!items.length && <div className="empty">Save a message from its ⋯ menu, with or without a reminder.</div>}
      {items.map((s) => {
        const m = byId[s.messageId];
        return (
          <div key={s.messageId} className="lrow">
            <span className={`when${s.remindAt && !s.reminded && new Date(s.remindAt) < new Date() ? " late" : ""}`}>
              {s.remindAt ? `${s.reminded ? "Reminded" : "Remind"} ${new Date(s.remindAt).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : "Saved"}
            </span>
            {m ? <Hit m={m} /> : <div className="empty">Loading…</div>}
            <button className="btn" onClick={() => done(s.messageId)}>Done</button>
          </div>
        );
      })}
    </Page>
  );
}

/** All unreads: every chat with something new, its unread messages, and "Mark read". */
export function UnreadsPage() {
  const me = useMe();
  const states = useStore((s) => s.states);
  const rooms = useStore((s) => s.rooms);
  const msgs = useStore((s) => s.msgs);
  const users = useStore((s) => s.users);
  const unread = Object.values(states).filter((s) => rooms[s.roomId] && s.unread > 0 && s.notify !== "none");
  useEffect(() => { for (const s of unread) if (!msgs[s.roomId]) loadRoom(s.roomId).catch(() => {}); }, [unread.length]);
  const markRead = (roomId: number, lastId: number) => { setRead(roomId, lastId); api.read(roomId, lastId).catch(() => {}); };
  return (
    <Page title="All unreads" right={unread.length > 1 ? <button className="lnk" onClick={() => unread.forEach((s) => markRead(s.roomId, s.lastId))}>Mark all read</button> : undefined}>
      {!unread.length && <div className="empty">You're all caught up.</div>}
      {unread.map((s) => {
        const list = (msgs[s.roomId] ?? []).filter((m) => inChat(m) && m.id > s.lastReadId && m.userId !== me.id && !m.deletedAt);
        return (
          <div key={s.roomId} className="ublock">
            <div className="uh"><button className="lnk" onClick={() => go({ v: "room", id: s.roomId })}><b>{roomTitle(rooms[s.roomId], me.id, users)}</b></button>
              <span>{s.unread} new</span><button className="lnk" onClick={() => markRead(s.roomId, s.lastId)}>Mark read</button></div>
            {list.map((m) => <Hit key={m.id} m={m} />)}
          </div>
        );
      })}
    </Page>
  );
}
